import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CommercialTerms, ComplexService } from '../services/complex.service';

/**
 * The venue's own commercial terms and whether the Partner Terms still need signing.
 *
 * Loaded once per session so the gate guard and the acceptance page share a single fetch: the
 * guard decides on the same payload the page then renders, which is the only way the two can
 * never disagree about whether a signature is outstanding.
 */
@Injectable({ providedIn: 'root' })
export class PartnerTermsStore {
  private readonly complexService = inject(ComplexService);

  private readonly termsSignal = signal<CommercialTerms | null>(null);
  readonly terms = this.termsSignal.asReadonly();
  readonly loaded = signal(false);

  /**
   * Whether a signature is outstanding.
   *
   * False while unknown, and false again if the read failed. A dropped connection is not evidence
   * that a venue has not signed, and locking an owner out of their own calendar over a status call
   * would be a far worse outcome than a signature collected one session late.
   */
  readonly needsAcceptance = computed(() => this.termsSignal()?.needsAcceptance ?? false);

  /**
   * Whether the owner has actively refused the terms now in force.
   *
   * Server state, not a browser flag. Held only in this app it would survive exactly as long as
   * the tab: the owner would sign out of their own blocked session, sign back in as a manager, and
   * the question we stopped to ask would have cost one click to ignore.
   *
   * False on an unknown state, for the same reason as above.
   */
  readonly declined = computed(() => this.termsSignal()?.declined ?? false);

  private loadPromise: Promise<void> | null = null;

  /** Idempotent: concurrent callers share one request; later callers get the cached result. */
  load(): Promise<void> {
    return (this.loadPromise ??= this.doLoad());
  }

  /** Forces a refetch — used right after accepting, where the cached copy still says unsigned. */
  reload(): Promise<void> {
    this.loadPromise = this.doLoad();
    return this.loadPromise;
  }

  /** Cleared on sign-out so the next admin on this browser is not judged on the last one's terms. */
  reset(): void {
    this.loadPromise = null;
    this.termsSignal.set(null);
    this.loaded.set(false);
  }

  private async doLoad(): Promise<void> {
    try {
      this.termsSignal.set(await firstValueFrom(this.complexService.getCommercialTerms()));
    } catch {
      this.termsSignal.set(null);
    } finally {
      this.loaded.set(true);
    }
  }
}
